// weatherVideo.ts — fetches and decodes a planet's baked seasonal weather video from
// GET /api/planet/{id}/weather (server climate.EncodeCloudVideo). The body is a zlib-deflated
// header + a delta-encoded sequence of uint8 cloud-cover frames ("FCV1"): frame 0 is stored
// raw, each later frame as a per-cell byte difference from the previous (uint8 wraparound) —
// cheap "video" compression that exploits the frames' temporal coherence as the precipitation
// bands migrate around the orbit. We inflate, undo the deltas, and expose the frames as the 0..1
// cloud-cover planes the focused planet interpolates by orbital phase. Cached + in-flight-deduped
// per planet id, like the climate raster, so re-focusing is instant.
import {
  ClampToEdgeWrapping,
  DataTexture,
  LinearFilter,
  RedFormat,
  RepeatWrapping,
  UnsignedByteType,
} from 'three';
import { scopedUrl } from '../../state/scope';

export interface CloudVideo {
  nx: number;
  ny: number;
  /** One equirect uint8 cloud-cover plane per month, row-major (row 0 = +90° lat,
   *  col 0 = -180° lon), length nx*ny. The client lerps between two by season phase. */
  frames: Uint8Array[];
}

// buildWeatherTexture makes the single R8 equirect texture that the cloud deck AND its shadows
// both sample — owned by Planet so there is exactly one interpolated field, guaranteeing the
// shadows track the visible clouds. A 1×1 placeholder when the world has no baked weather.
export function buildWeatherTexture(video: CloudVideo | null): DataTexture {
  const nx = video?.nx ?? 1;
  const ny = video?.ny ?? 1;
  const tex = new DataTexture(new Uint8Array(nx * ny), nx, ny, RedFormat, UnsignedByteType);
  tex.wrapS = RepeatWrapping; // longitude wraps
  tex.wrapT = ClampToEdgeWrapping; // latitude clamps at the poles
  tex.minFilter = LinearFilter;
  tex.magFilter = LinearFilter;
  tex.generateMipmaps = false;
  if (video) (tex.image.data as Uint8Array).set(video.frames[0]); // seed before first lerp
  tex.needsUpdate = true;
  return tex;
}

// lerpWeatherInto blends the two months bracketing seasonPhase (0..1 around the orbit) into the
// texture bytes and flags it for re-upload. Deck and shadows call this on the SAME texture, so
// there is a single seasonal field for both.
export function lerpWeatherInto(tex: DataTexture, video: CloudVideo, seasonPhase: number): void {
  const n = video.frames.length;
  const fpos = ((((seasonPhase % 1) + 1) % 1) * n) % n;
  const fa = Math.floor(fpos) % n;
  const fb = (fa + 1) % n;
  const t = fpos - Math.floor(fpos);
  const a = video.frames[fa];
  const b = video.frames[fb];
  const dst = tex.image.data as Uint8Array;
  for (let k = 0; k < dst.length; k++) {
    dst[k] = (a[k] + (b[k] - a[k]) * t) | 0;
  }
  tex.needsUpdate = true;
}

// Surfaceless kinds have no weather (server returns 404, mirrors galaxy.climateWorthy); skip
// the fetch. Kept in sync with climateRaster.SURFACELESS.
const SURFACELESS = new Set<string>(['gas', 'molten', 'broken', 'barren_dry', 'barren_cold']);

/** Whether a planet kind has a baked weather video to fetch. */
export function hasWeather(kind: string): boolean {
  return !SURFACELESS.has(kind);
}

const cache = new Map<string, Promise<CloudVideo | null>>();

/** Fetch a planet's weather video (null for surfaceless bodies / unknown ids). */
export function fetchPlanetWeather(id: string): Promise<CloudVideo | null> {
  let p = cache.get(id);
  if (!p) {
    p = load(id).catch((e) => {
      cache.delete(id); // let a transient failure be retried
      throw e;
    });
    cache.set(id, p);
  }
  return p;
}

async function load(id: string): Promise<CloudVideo | null> {
  const res = await fetch(scopedUrl(`/api/planet/${encodeURIComponent(id)}/weather`), {
    headers: { Accept: 'application/octet-stream' },
  });
  if (res.status === 404) return null; // surfaceless body or unknown id
  if (!res.ok) throw new Error(`weather ${id}: HTTP ${res.status}`);
  return parse(await inflate(await res.arrayBuffer()));
}

async function inflate(buf: ArrayBuffer): Promise<ArrayBuffer> {
  // Go's compress/zlib emits the zlib format (RFC 1950) -> DecompressionStream('deflate').
  const stream = new Response(buf).body!.pipeThrough(new DecompressionStream('deflate'));
  return new Response(stream).arrayBuffer();
}

function parse(buf: ArrayBuffer): CloudVideo {
  const dv = new DataView(buf);
  const magic = String.fromCharCode(dv.getUint8(0), dv.getUint8(1), dv.getUint8(2), dv.getUint8(3));
  if (magic !== 'FCV1') {
    throw new Error(`weather: bad magic "${magic}"`);
  }
  const nx = dv.getUint16(4);
  const ny = dv.getUint16(6);
  const nFrames = dv.getUint8(8);
  const n = nx * ny;
  const bytes = new Uint8Array(buf);
  let off = 9;
  const frames: Uint8Array[] = [];
  const prev = new Uint8Array(n);
  for (let f = 0; f < nFrames; f++) {
    const cur = new Uint8Array(n);
    for (let k = 0; k < n; k++) {
      // Frame 0 is raw; later frames are per-cell deltas from the previous (undo the
      // uint8 wraparound the encoder relied on, so cur stays a true cloud-cover byte).
      cur[k] = f === 0 ? bytes[off] : (prev[k] + bytes[off]) & 0xff;
      off++;
    }
    frames.push(cur);
    prev.set(cur);
  }
  return { nx, ny, frames };
}
